import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Tag, Plus, Copy } from "@phosphor-icons/react";
import { couponLabel } from "./CouponBox";

const EMPTY = { code: "", kind: "percent", value: "", duration: "once", duration_in_months: "3", max_redemptions: "", expires_at: "" };

export function AdminCoupons() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code, duration: form.duration,
        percent_off: form.kind === "percent" ? Number(form.value) : null,
        amount_off: form.kind === "amount" ? Number(form.value) : null,
        duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) : null,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
      });
      toast.success("Cupom criado"); setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.put(`/admin/coupons/${c.id}`, { active: !c.enabled }); toast.success(c.enabled ? "Cupom desativado" : "Cupom ativado"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Código copiado"); };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mt-8" data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-semibold text-lg text-slate-900 flex items-center gap-2"><Tag size={20} weight="duotone" className="text-indigo-600" /> Cupons de desconto</h3>
        {!open && <Button size="sm" onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="coupon-new"><Plus size={14} className="mr-1" /> Novo cupom</Button>}
      </div>
      {open && (
        <form onSubmit={create} className="card-crisp p-5 mb-4 grid sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end" data-testid="coupon-form">
          <div className="lg:col-span-2"><Label>Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="Ex: BEMVINDO20" required className="mt-1 font-mono-nums" data-testid="coupon-code" /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="mt-1" data-testid="coupon-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="percent">Percentual (%)</SelectItem><SelectItem value="amount">Valor (R$)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>{form.kind === "percent" ? "Desconto (%)" : "Desconto (R$)"}</Label><Input type="number" step="0.01" min="0.01" max={form.kind === "percent" ? 100 : undefined} value={form.value} onChange={f("value")} required className="mt-1" data-testid="coupon-value" /></div>
          <div>
            <Label>Duração</Label>
            <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
              <SelectTrigger className="mt-1" data-testid="coupon-duration"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="once">Primeira cobrança</SelectItem><SelectItem value="repeating">Por alguns meses</SelectItem><SelectItem value="forever">Para sempre</SelectItem></SelectContent>
            </Select>
          </div>
          {form.duration === "repeating" && <div><Label>Meses</Label><Input type="number" min="1" value={form.duration_in_months} onChange={f("duration_in_months")} className="mt-1" data-testid="coupon-months" /></div>}
          <div><Label>Limite de usos</Label><Input type="number" min="1" value={form.max_redemptions} onChange={f("max_redemptions")} placeholder="Ilimitado" className="mt-1" data-testid="coupon-max" /></div>
          <div><Label>Expira em</Label><Input type="date" value={form.expires_at} onChange={f("expires_at")} className="mt-1" data-testid="coupon-expires" /></div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="coupon-save">{saving ? "Criando..." : "Criar"}</Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </form>
      )}
      <div className="card-crisp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="px-5 py-3 font-medium">Código</th><th className="px-5 py-3 font-medium">Desconto</th><th className="px-5 py-3 font-medium">Usos</th><th className="px-5 py-3 font-medium">Expira</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3" /></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500">Carregando cupons...</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500">Nenhum cupom criado ainda.</td></tr>}
            {rows?.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`coupon-row-${c.code}`}>
                <td className="px-5 py-3"><button onClick={() => copy(c.code)} className="inline-flex items-center gap-1 font-mono-nums font-bold text-slate-900 hover:text-indigo-700" title="Copiar">{c.code} <Copy size={12} /></button>{c.name && c.name !== c.code && <div className="text-xs text-slate-500">{c.name}</div>}</td>
                <td className="px-5 py-3">{c.percent_off ? `${c.percent_off}%` : formatCurrency(c.amount_off || 0)}<div className="text-xs text-slate-500">{couponLabel(c).replace(/^.*?de desconto /, "")}</div></td>
                <td className="px-5 py-3 font-mono-nums">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                <td className="px-5 py-3 text-slate-600">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`coupon-status-${c.code}`}>{c.active ? "Ativo" : c.expired ? "Expirado" : c.exhausted ? "Esgotado" : "Desativado"}</span></td>
                <td className="px-5 py-3 text-right">{!c.expired && !c.exhausted && <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`coupon-toggle-${c.code}`}>{c.enabled ? "Desativar" : "Ativar"}</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">Os cupons são criados no Stripe. O cliente aplica em <strong>Meu Plano</strong> (vê o preço com desconto antes de pagar) ou digita direto no checkout.</p>
    </div>
  );
}
